/**
 * Bounded-concurrency map, shared by the Legal pages.
 *
 * It lives here rather than inside a page because BOTH pages that need it make
 * one request per row: the Agreements list expands every template into its
 * version chain and its envelope counts, and the Envelopes tab expands every
 * envelope into its signers. Firing those unbounded opens a socket per row and
 * the browser queues them anyway — the pool just makes the limit ours and
 * explicit rather than the browser's and invisible.
 *
 * `fn` must not throw: a rejection here rejects the whole `Promise.all` and
 * loses every result, including the ones that succeeded. Callers catch per item
 * and return a value that RECORDS the failure, because a row whose fetch failed
 * and a row whose real answer is 0 must not render the same way.
 */

/** Six at a time — roughly the browser's own per-host cap, so nothing queues twice. */
export const POOL = 6;

export async function mapPool<T, R>(
  items: readonly T[], limit: number, fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let cursor = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (;;) {
      const i = cursor;
      cursor += 1;
      if (i >= items.length) return;
      out[i] = await fn(items[i]);
    }
  });
  await Promise.all(workers);
  return out;
}

export default mapPool;
